import { Flex } from '@bookjane2/bookjane-design-library';
import {
  BKJAvatarStyled,
  ColorDot,
  EditedFields,
  InlineBKJButton,
  MobileTableCell,
  MobileTableRowViewDetailsCell,
  AuditTrailActivityTabButton,
  MobileAuditTrailContainer,
  MobileActivityDate,
  AwardedShift,
  ActivityReasonMessage,
} from 'components/ActivityTab/ActivityTab.styled';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { useTranslation } from 'hooks/useTranslation';
import { useActivityTabRowPropsContext } from 'components/ActivityTab/ActivityTabRow.props.context';
import { MobileAuditTrailActivityCell } from 'components/AuditTrailActivityTab/AuditTrailActivityTab.styled';
import { FC, Fragment, useCallback } from 'react';
import { MountTransition } from 'routes';
import { Jane, User } from 'types/graphql';
import {
  ActivityTypeEnum,
  extractActivityType,
} from 'components/AuditTrailActivityTab/AuditTrailActivityTab.constants';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalDataContext';

export const MobileAuditTrailActivityTabRow: FC = () => {
  const { t, i18n } = useTranslation();
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const {
    activityDate,
    calloutListId,
    dotColor,
    eventName,
    eventType,
    hasEdits,
    hasReason,
    isAddedToBlock,
    reasonMessage,
    sentToNrOfAgencies,
    showHideDetails,
    hasReasonCategory,
    reasonCategory,
    userDetails,
    editDetailList,
    detailsShown,
    awardedTo,
  } = useActivityTabRowPropsContext();
  const goToAuditTrailView = useCallback(
    () =>
      window.j360.shiftModalRoutingService.goToView('AuditTrailView', {
        calloutListId,
      }),
    [calloutListId],
  );

  const activityType = extractActivityType({
    eventName,
    userFirstName: userDetails?.firstName,
    userLastName: userDetails?.lastName,
  });

  const GetActivityViewComponent = () => {
    const showPendingSignUp = !!(userDetails as Jane)?.pendingSignup;

    const title = (userDetails as User)?.title;

    const communityName = (userDetails as Jane | User)?.communityName;

    switch (activityType) {
      case ActivityTypeEnum.CALLOUT:
        if (order.isExternal) {
          return null;
        }
        return (
          <AuditTrailActivityTabButton onClick={goToAuditTrailView} variant="PurpleOutlined">
            {t('schedule:VIEW')}
          </AuditTrailActivityTabButton>
        );
      case ActivityTypeEnum.REGULAR:
        return (
          <Flex alignItems="center" gap="8px">
            <Fragment>
              <BKJAvatarStyled
                width="30px"
                height="30px"
                fallbackAvatarColor="TextDarkGrey"
                src={userDetails?.avatarUrls?.origin}
                showPendingSignUp={showPendingSignUp}
              />
              <Flex flexDirection="column">
                <div>{`${userDetails.firstName} ${userDetails.lastName}`}</div>
                <div>{title}</div>
              </Flex>
            </Fragment>
          </Flex>
        );
      case ActivityTypeEnum.BIDDING:
        return (
          <Flex alignItems="center" gap="8px">
            <Fragment>
              <BKJAvatarStyled
                width="30px"
                height="30px"
                fallbackAvatarColor="TextDarkGrey"
                src={userDetails?.avatarUrls?.origin}
                showPendingSignUp={showPendingSignUp}
              />
              <Flex flexDirection="column">
                <div>{`${userDetails?.firstName} ${userDetails?.lastName}`}</div>
                <div>{communityName}</div>
              </Flex>
            </Fragment>
          </Flex>
        );
      case ActivityTypeEnum.AUTOMATED:
        return t('status:AUTOMATED');
    }
  };
  return (
    <MobileAuditTrailContainer>
      <MobileActivityDate>{activityDate}</MobileActivityDate>
      <MobileAuditTrailActivityCell>
        <Flex alignItems="flex-start" gap="8px" width="100%">
          <ColorDot color={dotColor}>•</ColorDot>
          <Flex flexDirection="column" justifyContent="center" width="100%">
            <Flex justifyContent="space-between">
              <MobileTableCell>
                {getTranslatedResponse(eventType, 'status', i18n, t)}{' '}
                {eventName === 'awarded_shift' && <AwardedShift>{awardedTo}</AwardedShift>}
              </MobileTableCell>
              {hasEdits && (
                <EditedFields>{`${editDetailList.length} ${t('dashboard:CHANGES')}`}</EditedFields>
              )}
            </Flex>
            {isAddedToBlock && <EditedFields>{t('common:ADDED_TO_BLOCK')}</EditedFields>}
            {sentToNrOfAgencies > 0 && (
              <EditedFields>
                {sentToNrOfAgencies} {t('common:AGENCY_MEMBERS')}
              </EditedFields>
            )}
            {hasEdits && (
              <InlineBKJButton variant="PurpleText" onClick={showHideDetails}>
                {detailsShown ? t('dashboard:HIDE_DETAILS') : t('common:VIEW_DETAILS')}
              </InlineBKJButton>
            )}
          </Flex>
        </Flex>
      </MobileAuditTrailActivityCell>
      <MobileTableCell>{GetActivityViewComponent()}</MobileTableCell>
      {hasReason ? (
        <MobileTableRowViewDetailsCell>
          <ActivityReasonMessage>
            {t('dashboard:REASON')}: {reasonCategory ? reasonCategory : reasonMessage}
          </ActivityReasonMessage>
        </MobileTableRowViewDetailsCell>
      ) : null}
      {detailsShown && hasEdits ? (
        <MobileTableRowViewDetailsCell>
          <MountTransition>
            <div>{!hasReason && !!reasonMessage && reasonMessage}</div>
            <div>{hasReasonCategory && reasonCategory}</div>
            {editDetailList?.map((edit) => (
              <div style={{ wordBreak: 'break-word' }}>{edit}</div>
            ))}
          </MountTransition>
        </MobileTableRowViewDetailsCell>
      ) : null}
    </MobileAuditTrailContainer>
  );
};
